<template>
  <TransitionRoot as="template" :show="props.isOpen">
    <Dialog class="relative z-10" @close="emits('close-bar')">
      <TransitionChild
        as="template"
        enter="ease-in-out duration-500"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in-out duration-500"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-neutral_950 bg-opacity-30 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-10">
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
            >
              <DialogPanel class="pointer-events-auto w-screen pb-11 sm:pb-0 sm:max-w-[560px]">
                <div
                  class="flex h-full flex-col overflow-y-scroll bg-neutral_950 sm:bg-neutral_800 shadow-xl"
                >
                  <div class="relative flex flex-col items-center gap-2 py-10 px-4 sm:px-8">
                    <div class="flex w-full items-center justify-between mt-4 pb-3">
                      <p
                        class="text-[12px] uppercase text-base_white font-bold leading-[150%] tracking-[2.4px]"
                      >
                        {{ $t('gradeBettingShop') }}
                      </p>
                      <ClientOnly>
                        <ion-icon
                          class="text-[20px] text-base_white sm:text-base cursor-pointer"
                          name="close-outline"
                          @click="emits('close-bar')"
                        />
                      </ClientOnly>
                    </div>
                    <div v-for="(i, index) in 5" :key="index" class="flex w-full flex-col gap-2">
                      <BettingShopReviewSection
                        v-model="reviewValues[index].reviewScore"
                        :index="i"
                        @input="handleSaveReviewToStorage"
                      />
                      <div v-if="index == 4" class="h-px bg-primary_800" />
                    </div>
                    <InputTextArea
                      v-model="reviewText"
                      :placeholder="`${$t('yourCommentAboutBettingShop')}`"
                      @blur="handleSaveReviewToStorage"
                    />
                    <div class="h-px w-full bg-primary_800 hidden sm:block" />
                    <ButtonComponent
                      v-if="!commonStore.isMobile"
                      :is-loading="isLoading"
                      :text="`${$t('gradeAndComment')}`"
                      streched
                      cropped
                      size="md"
                      @click="createReview"
                    />
                  </div>
                </div>
                <MobileBottomAppBar v-if="commonStore.isMobile">
                  <ButtonComponent
                    :is-loading="isLoading"
                    :text="`${$t('gradeAndComment')}`"
                    size="md"
                    @click="createReview"
                  />
                </MobileBottomAppBar>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { bettingShopRepository } from '~/repositories/bettingShopsRepository';
import { useCommonStore } from '~/store/commonStore';
import { useDialogStore } from '~/store/dialogStore';
import { useUserStore } from '~/store/user';
import { type BettingShopReviewStorage } from '~/interfaces';

const userStore = useUserStore();

const {
  value: reviewStorageObject,
  saveValue: saveReview,
  loadValue: loadReview,
  clearValue: clearReview
} = useTmpLocalStorage<BettingShopReviewStorage>('bettingShopReviw');

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  bettingShopId: {
    type: Number,
    required: true
  }
});

const INIT_VALUES = [
  { reviewType: 'VALUE1', reviewScore: 3 },
  { reviewType: 'VALUE2', reviewScore: 3 },
  { reviewType: 'VALUE3', reviewScore: 3 },
  { reviewType: 'VALUE4', reviewScore: 3 },
  { reviewType: 'VALUE5', reviewScore: 3 }
];

// const router = useRouter();
// const localePath = useLocalePath();
const emits = defineEmits(['close-bar', 'review-created']);
const { $api } = useNuxtApp();
const bettingShopRepo = bettingShopRepository($api);
const commonStore = useCommonStore();
const dialogStore = useDialogStore();
const { t } = useI18n();
const reviewText = ref('');
const isLoading = ref(false);
const reviewValues = ref([...INIT_VALUES]);

onMounted(() => {
  loadReview();

  if (reviewStorageObject.value) {
    reviewText.value = reviewStorageObject.value?.reviewText || '';
    reviewValues.value = reviewStorageObject.value?.reviewValues || [...INIT_VALUES];
  }
});

const handleSaveReviewToStorage = () => {
  saveReview({ reviewText: reviewText.value, reviewValues: reviewValues.value });
};

const createReview = async () => {
  try {
    if (!userStore.user?.id) {
      emits('close-bar');
      dialogStore.createLoginDialog();
      return;
    }
    isLoading.value = true;
    const review = await bettingShopRepo.createBettingShopReview(
      props.bettingShopId,
      reviewText.value,
      reviewValues.value
    );
    isLoading.value = false;
    reviewValues.value.forEach((v) => {
      v.reviewScore = 3;
    });
    reviewText.value = '';
    clearReview();
    emits('review-created', review);
    emits('close-bar');
  } catch (err) {
    isLoading.value = false;
    emits('close-bar');
    if (err.response && err.response.status === 401) {
      // router.push(localePath('/register'));
    } else {
      nextTick(() => {
        dialogStore.createToasterNotification(
          'error',
          `${t('error')}`,
          `${t('alreadyWrittenReview')}`
        );
      });
    }
  }
};
</script>
